import { z } from "zod";

// Product validation schema
export const productSchema = z.object({
  name: z.string().min(1, "Product name is required").max(255, "Name too long"),
  description: z.string().max(1000, "Description too long").optional(),
  size: z.string().max(100, "Size too long").optional(),
  dimensions: z.string().max(255, "Dimensions too long").optional(),
  weight: z.number().positive("Weight must be positive").optional().nullable(),
  color: z.string().max(100, "Color too long").optional(),
  material: z.string().max(200, "Material too long").optional(),
  brand: z.string().max(200, "Brand too long").optional(),
  available_stock: z.number().int("Stock must be whole number").min(0, "Stock cannot be negative"),
  price: z.number().positive("Price must be positive").max(999999.99, "Price too high"),
});

// Service validation schema
export const serviceSchema = z.object({
  name: z.string().min(1, "Service name is required").max(255, "Name too long"),
  description: z.string().max(1000, "Description too long").optional(),
  thumbnail_image: z.string().url("Invalid URL format").optional().or(z.literal("")),
});

// Enhanced image URL validation with security considerations
export const imageUrlSchema = z
  .string()
  .url("Invalid URL format")
  .refine(
    (url) => {
      try {
        const parsedUrl = new URL(url);

        // Only allow http/https protocols (prevents javascript:, data:, file: schemes)
        if (!["http:", "https:"].includes(parsedUrl.protocol)) {
          return false;
        }

        // Block localhost and private IP ranges in production
        if (import.meta.env.VITE_APP_ENV === "production") {
          const hostname = parsedUrl.hostname.toLowerCase();

          // Block localhost variants
          if (["localhost", "127.0.0.1", "::1"].includes(hostname)) {
            return false;
          }

          // Block private IP ranges (basic check)
          if (hostname.match(/^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.)/)) {
            return false;
          }

          // Block special domains
          if (hostname.includes(".local") || hostname === "metadata.google.internal") {
            return false;
          }
        }

        // Check for valid image extensions
        const pathname = parsedUrl.pathname.toLowerCase();
        const hasValidExtension = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/.test(pathname);

        // Additional check: URL should not be excessively long
        if (url.length > 2048) {
          return false;
        }

        return hasValidExtension;
      } catch {
        return false;
      }
    },
    {
      message:
        "URL must be a valid HTTPS/HTTP image URL with supported format (jpg, jpeg, png, gif, webp, svg) and cannot point to private networks",
    }
  );

// Validate and sanitize image URL
export const validateImageUrl = (url: string): { isValid: boolean; error?: string } => {
  try {
    imageUrlSchema.parse(url);
    return { isValid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { isValid: false, error: error.errors[0]?.message || "Invalid image URL" };
    }
    return { isValid: false, error: "Invalid image URL" };
  }
};

// Type definitions for validated data
export type ValidatedProduct = z.infer<typeof productSchema>;
export type ValidatedService = z.infer<typeof serviceSchema>;

// === Dynamic Form Component Validation ===

import type { ChecklistResponseValue } from "@/types/quote.types";

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export interface ValidationRule {
  type: "required" | "minLength" | "maxLength" | "pattern" | "min" | "max";
  value?: number | string;
  message: string;
}

// Sanitize input to prevent XSS
export function sanitizeInput(input: string): string {
  return input
    .replace(/&/g, "&amp;") // MUST be first to prevent double-encoding
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
}

// Parse validation rules from string format
export function parseValidationRules(rulesString?: string): ValidationRule[] {
  if (!rulesString) return [];

  try {
    const parsed = JSON.parse(rulesString);
    return Array.isArray(parsed) ? (parsed as ValidationRule[]) : [];
  } catch {
    return [];
  }
}

// Validate a single field value
export function validateField(
  value: ChecklistResponseValue,
  rules: ValidationRule[],
  isRequired = false
): ValidationResult {
  // Check required validation
  if (isRequired) {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return {
        isValid: false,
        error: "This field is required",
      };
    }
  }

  // If not required and empty, it's valid
  if (!isRequired && (value === "" || value === undefined || value === null)) {
    return { isValid: true };
  }

  // Sanitize string inputs to prevent XSS
  if (typeof value === "string") {
    const sanitizedValue = sanitizeInput(value);
    if (sanitizedValue !== value) {
      return {
        isValid: false,
        error: "Input contains invalid characters",
      };
    }
  }

  // Apply validation rules
  for (const rule of rules) {
    const result = applyValidationRule(value, rule);
    if (!result.isValid) {
      return result;
    }
  }

  return { isValid: true };
}

function applyValidationRule(
  value: ChecklistResponseValue,
  rule: ValidationRule
): ValidationResult {
  switch (rule.type) {
    case "minLength":
      if (typeof value === "string" && rule.value && value.length < rule.value) {
        return {
          isValid: false,
          error: rule.message || `Minimum length is ${rule.value} characters`,
        };
      }
      break;

    case "maxLength":
      if (typeof value === "string" && rule.value && value.length > rule.value) {
        return {
          isValid: false,
          error: rule.message || `Maximum length is ${rule.value} characters`,
        };
      }
      break;

    case "pattern":
      if (typeof value === "string" && rule.value) {
        try {
          const regex = new RegExp(rule.value as string);
          if (!regex.test(value)) {
            return {
              isValid: false,
              error: rule.message || "Invalid format",
            };
          }
        } catch {
          return {
            isValid: false,
            error: "Invalid validation pattern",
          };
        }
      }
      break;

    case "min": {
      const numValue = typeof value === "string" ? Number.parseFloat(value) : value;
      if (typeof numValue === "number" && rule.value && numValue < rule.value) {
        return {
          isValid: false,
          error: rule.message || `Minimum value is ${rule.value}`,
        };
      }
      break;
    }

    case "max": {
      const maxNumValue = typeof value === "string" ? Number.parseFloat(value) : value;
      if (typeof maxNumValue === "number" && rule.value && maxNumValue > rule.value) {
        return {
          isValid: false,
          error: rule.message || `Maximum value is ${rule.value}`,
        };
      }
      break;
    }

    default:
      break;
  }

  return { isValid: true };
}

// Validate all component values in a task
export function validateChecklistComponents(
  componentValues: Record<string, ChecklistResponseValue>,
  components: Array<{
    field_name: string;
    is_required?: boolean;
    required?: boolean;
    validation_rules?: string;
  }>
): Record<string, ValidationResult> {
  const validationResults: Record<string, ValidationResult> = {};

  for (const component of components) {
    const value = componentValues[component.field_name];
    const isRequired = component.is_required ?? component.required ?? false;
    const rules = parseValidationRules(component.validation_rules);

    validationResults[component.field_name] = validateField(value, rules, isRequired);
  }

  return validationResults;
}

// Check if all validations are passing
export function areAllValidationsValid(
  validationResults: Record<string, ValidationResult>
): boolean {
  return Object.values(validationResults).every((result) => result.isValid);
}

// === CSS Sanitization for Chart Components ===

// Sanitize CSS color values to prevent CSS injection
export function sanitizeCssColor(color: string): string | null {
  if (!color || typeof color !== "string") {
    return null;
  }

  // Remove any potential CSS injection attempts
  const cleanColor = color.trim();

  // Allow only safe CSS color formats
  const validColorPatterns = [
    /^#[0-9A-Fa-f]{3}$/, // #RGB
    /^#[0-9A-Fa-f]{6}$/, // #RRGGBB
    /^#[0-9A-Fa-f]{8}$/, // #RRGGBBAA
    /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/, // rgb(r,g,b)
    /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[0-9.]+\s*\)$/, // rgba(r,g,b,a)
    /^hsl\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*\)$/, // hsl(h,s,l)
    /^hsla\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*,\s*[0-9.]+\s*\)$/, // hsla(h,s,l,a)
    /^var\(--[a-zA-Z0-9-]+\)$/, // CSS variables
  ];

  // Named colors (safe subset)
  const namedColors = new Set([
    "transparent",
    "currentColor",
    "inherit",
    "initial",
    "unset",
    "black",
    "white",
    "red",
    "green",
    "blue",
    "yellow",
    "orange",
    "purple",
    "pink",
    "brown",
    "gray",
    "grey",
    "cyan",
    "magenta",
    "lime",
    "navy",
    "teal",
    "silver",
    "maroon",
    "olive",
    "aqua",
    "fuchsia",
  ]);

  // Check if it's a valid color pattern or named color
  const isValidPattern = validColorPatterns.some((pattern) => pattern.test(cleanColor));
  const isValidNamedColor = namedColors.has(cleanColor.toLowerCase());

  if (isValidPattern || isValidNamedColor) {
    return cleanColor;
  }

  // If it doesn't match any safe pattern, return null to skip this color
  return null;
}

// Sanitize CSS property key to prevent injection
export function sanitizeCssKey(key: string): string | null {
  if (!key || typeof key !== "string") {
    return null;
  }

  // Only allow alphanumeric characters, hyphens, and underscores
  const cleanKey = key.trim();
  if (/^[a-zA-Z0-9-_]+$/.test(cleanKey)) {
    return cleanKey;
  }

  return null;
}
